import { GfCompositeError } from '../errors';
import { isDef, isFunc } from '../utils/common';
import { CPRSignal } from '../utils/CPRController';
import { Deferred } from './Deferred';

export interface ParallelOptions {
	// Defaults to 1
	concurrency?: number;

	// By default, the parallel functions will wait for any ongoing operations before rejecting
	returnImmediatelyOnErrorOrCancel?: boolean;

	cprSignal?: CPRSignal;

	errMsg?: string;
}

export type FnMapper<T, U> = (item: T, index: number) => Promise<U> | U;
export type FnResult<U> = (result: U, index: number) => void;

/**
 * Allows async processing of an iterable; returns results to a second callback.
 * Result order is not guaranteed to match input order
 *
 * Marked as async, even though it doesn't need to be, so that the initial errors are not sync
 * https://stackoverflow.com/questions/21887856/should-an-async-api-ever-throw-synchronously
 *
 * TODO: Remove deprecated overrides?
 */
/**
 * Options before one or two callbacks
 * @param iterable
 * @param options
 * @param fnMapper
 * @param fnResult
 */
export async function piterate<T, U>(
	iterable: Iterable<T>,
	options: ParallelOptions | undefined,
	fnMapper: FnMapper<T, U>,
	fnResult?: FnResult<U>
): Promise<void>;
/**
 * No options, one or two callbacks
 * @param iterable
 * @param fnMapper
 * @param options
 */
export async function piterate<T, U>(
	iterable: Iterable<T>,
	fnMapper: FnMapper<T, U>,
	fnResult?: FnResult<U>
): Promise<void>;
/**
 * One callback, with options after
 * @deprecated
 * @param iterable
 * @param fnMapper
 * @param options
 */
export async function piterate<T, U>(
	iterable: Iterable<T>,
	fnMapper: FnMapper<T, U>,
	options: ParallelOptions
): Promise<void>;
/**
 * Two callbacks, options after
 * @deprecated
 * @param iterable
 * @param fnMapper
 * @param fnResult
 * @param options
 */
export async function piterate<T, U>(
	iterable: Iterable<T>,
	fnMapper: FnMapper<T, U>,
	fnResult: FnResult<U>,
	options?: ParallelOptions
): Promise<void>;
export async function piterate<T, U>(
	iterable: Iterable<T>,
	arg1: ParallelOptions | undefined | FnMapper<T, U>,
	arg2?: FnMapper<T, U> | FnResult<U> | ParallelOptions,
	arg3?: FnResult<U> | ParallelOptions
): Promise<void> {
	// Extra complication for the overloads...
	let options: ParallelOptions | undefined;
	let fnMapper: FnMapper<T, U>;
	let fnResult: FnResult<U> | undefined;

	if (isFunc(arg1)) {
		fnMapper = arg1;

		if (isFunc(arg2)) {
			fnResult = arg2 as FnResult<U>;
			options = arg3 as ParallelOptions | undefined;
		} else {
			options = arg2 as ParallelOptions | undefined;
		}
	} else {
		options = arg1;
		fnMapper = arg2 as FnMapper<T, U>;
		fnResult = arg3 as FnResult<U>;
	}

	const { concurrency = 1, returnImmediatelyOnErrorOrCancel = false, cprSignal } = options ?? {};

	if (!((Number.isSafeInteger(concurrency) || concurrency === Infinity) && concurrency >= 1)) {
		throw new TypeError(
			`Expected 'concurrency' to be an integer from 1 and up or \`Infinity\`, got \`${concurrency}\` (${typeof concurrency})`
		);
	}

	if (cprSignal?.canceled) {
		if (isDef(cprSignal.reason)) {
			throw new GfCompositeError('cprSignal already canceled', [cprSignal.reason]);
		} else {
			// Just return immediately
			return Promise.resolve();
		}
	}

	let nextIndex = 0;
	let numWaiting = 0;
	let doneIteratingInput = false;
	let canceled: boolean;
	let errored: boolean;
	const errors: any[] = [];

	const iterator = iterable[Symbol.iterator]();

	const deferred = new Deferred<void>();

	const resolve = () => {
		cprSignal?.off('cancel', onCancel);
		cprSignal?.off('resume', onCancel);
		deferred.resolve();
	};

	const reject = (err: any) => {
		cprSignal?.off('cancel', onCancel);
		cprSignal?.off('resume', onCancel);
		deferred.reject(err);
	};

	const onCancel = () => {
		if (!canceled) {
			canceled = true;
			doneIteratingInput = true;

			if (returnImmediatelyOnErrorOrCancel) {
				if (isDef(cprSignal?.reason)) {
					reject(new GfCompositeError('cprSignal canceled', [cprSignal?.reason]));
				} else {
					// Soft cancel
					resolve();
				}
			} else {
				// We still want to wait for pending stuff...
				if (isDef(cprSignal?.reason)) {
					errors.push(cprSignal?.reason);
					rejectWithStoredErrorsIfNothingPending();
				} else if (numWaiting === 0) {
					// && doneIteratingInput) {
					// No operations are running, and no more are on the stack. Done!
					resolve();
				}
			}
		}
	};

	const onResume = () => {
		maybeRejectOrRunMore();
	};

	const rejectWithStoredErrorsIfNothingPending = () => {
		if (numWaiting === 0) {
			// We had pending things, but all are done so we can reject
			if (errors.length === 1) {
				reject(
					new GfCompositeError(
						options?.errMsg ?? 'piterate(): received single error',
						errors
					)
				);
			} else {
				reject(
					new GfCompositeError(
						options?.errMsg ?? 'piterate(): received multiple errors in parallel',
						errors
					)
				);
			}
		}
	};

	const onError = (err: any) => {
		errored = true;

		if (returnImmediatelyOnErrorOrCancel) {
			reject(err);
		} else {
			errors.push(err);
			rejectWithStoredErrorsIfNothingPending();
		}
	};

	const maybeRejectOrRunMore = () => {
		if (errors.length) {
			rejectWithStoredErrorsIfNothingPending();
		} else {
			maybeRunMore();
		}
	};

	const maybeRunMore = (): void => {
		while (
			!errored &&
			!canceled &&
			!doneIteratingInput &&
			numWaiting < concurrency &&
			!cprSignal?.paused
		) {
			const nextItem = iterator.next();

			if (nextItem.done) {
				doneIteratingInput = true;
			} else {
				// Do something with the input
				++numWaiting;

				// Fire off but don't wait
				const _index = nextIndex++;

				let valueOrPromise;

				try {
					valueOrPromise = fnMapper(nextItem.value, _index);
				} catch (syncError) {
					--numWaiting;
					onError(syncError);
					return;
				}

				Promise.resolve(valueOrPromise).then(
					result => {
						if (!errored && !canceled) {
							fnResult?.(result, _index);
						}

						--numWaiting;

						maybeRejectOrRunMore();
					},
					err => {
						--numWaiting;
						onError(err);
					}
				);
			}
		}

		if (numWaiting === 0 && doneIteratingInput) {
			// No operations are running, and no more to read, so either there were
			// no inputs, or they were sync & no errors...
			resolve();
		}
	};

	cprSignal?.on('cancel', onCancel);
	cprSignal?.on('resume', onResume);
	maybeRunMore();

	return deferred.promise;
}
